import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { styled, css, ThemeProvider, useTheme, ThemeContext } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { getDataTestId } from '@/js/libs/utils/get-data-test-id/index.ts';
import Button from '../../Controls/Button/index.js';
import Icons from '../../Elements/Icons/index.js';
import { ConfirmAlertStyles } from './ConfirmAlertStyles.js';
export const CloseButton = styled(Icons.Cancel).attrs((props) => ({
    color: props.theme.colors.grey05_1,
})) `
  position: absolute;
  right: 15px;
  top: 0;
  bottom: 0;
  margin: auto;
  cursor: pointer;
`;
export const Body = styled.div `
  position: relative;
  width: ${({ type }) => (type ? '600px' : '400px')};
  max-width: 100%;
  padding: ${({ type }) => (type ? '20px 54px 20px 20px' : '20px')};
  text-align: left;
  background: ${({ theme }) => theme.colors.grey00_1};
  border-radius: ${({ type }) => (type ? '0 0 3px 3px' : '3px')};
  color: ${({ theme }) => theme.colors.grey100_1};
  ${({ theme }) => theme.shadows.medium};
  ${({ type }) => type === 'error' &&
    css `
      border-top: 2px solid ${({ theme }) => theme.colors.critical100};
    `}
`;
export const Title = styled.div `
  font-weight: 500;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.black_1};
`;
export const Message = styled.div `
  margin-top: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black_1};
`;
export const ButtonsContainer = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ $active }) => $active && '20px'};
  flex-direction: row-reverse;
`;
export const ConfirmAlertBody = ({ type, title, body, buttons, message, withoutClose, theme, closeText, t, onClose, }) => {
    const fallbackTheme = useContext(ThemeContext);
    const effectiveTheme = theme ?? fallbackTheme;
    if (!effectiveTheme) {
        throw new Error('ConfirmAlert: theme is missing. Use this alert within a ThemeProvider.');
    }
    return (_jsxs(ThemeProvider, { theme: effectiveTheme, children: [_jsx(ConfirmAlertStyles, {}), _jsxs(Body, { type: type, "data-testid": 'confirm-alert-body', children: [type && withoutClose && _jsx(CloseButton, { onClick: onClose, "data-testid": 'close-button' }), _jsx(Title, { "data-testid": 'modal-alert-title', children: title }), _jsx(Message, { "data-testid": 'modal-alert-message', children: message }), body && _jsx("div", { children: body }), _jsxs(ButtonsContainer, { $active: !!buttons || !withoutClose, children: [buttons &&
                                buttons.map((btn) => (_jsx(Button, { onClick: () => btn.onClick(onClose), "data-testid": getDataTestId(btn.label, 'button'), children: btn.label }, btn.label))), !withoutClose && (_jsx(Button, { onClick: onClose, "data-testid": 'no-button', children: closeText || (t && t('messages:CANCEL')) }))] })] })] }));
};
const styledConfirmAlert = (options) => confirmAlert({
    ...options,
    buttons: options.buttons,
    customUI: ({ title, message, onClose }) => (_jsx(ThemeContext.Provider, { value: options.theme, children: _jsx(ConfirmAlertBody, { title: title, message: message, onClose: onClose, ...options }) })),
});
/**
 * @deprecated Please, use `ModalDialog` instead
 * @param options
 */
function useConfirmAlert() {
    const theme = useTheme();
    return useCallbackRef((opts) => styledConfirmAlert({ theme, ...opts }));
}
export default useConfirmAlert;
